import { useMutation, useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { UseMutationOptions } from 'react-query/types/react/types'
import { ExternalIntegration, ExternalIntegrationsClient } from 'api/clients/ExternalIntegrationsClient'
import { Data } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { calendarKeys } from '../calendar/calendarKeys'
import { scheduledPostsKeys } from '../scheduledPosts/scheduledPostsKeys'

export type Options = Omit<UseMutationOptions<void, Error, void>, 'mutationFn' | 'mutationKey'>

export const useDisconnectMultipleIntegrations = (integrations: ExternalIntegration[], options?: Options) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const ids = integrations.map((integration) => integration.id)

  return useMutation(
    async () => {
      await ExternalIntegrationsClient.bulkDestroyExternalIntegrations({
        workspaceId,
        ids: integrations.map((integration) => integration.id),
      })
    },
    {
      ...options,
      onMutate: async () => {
        await queryClient.cancelQueries(['externalIntegrations', workspaceId])
        await queryClient.cancelQueries(['externalIntegrations', workspaceId, 'metrics'])
        const previousIntegrations = queryClient.getQueryData(['externalIntegrations', workspaceId])
        const previousIntegrationsWithMetrics = queryClient.getQueryData([
          'externalIntegrations',
          workspaceId,
          'metrics',
        ])

        queryClient.setQueryData(['externalIntegrations', workspaceId], (old: Data | undefined) => {
          const updatedIntegrations =
            old?.external_integrations?.filter((i: ExternalIntegration) => !ids.includes(i.id)) || []

          return { ...old, external_integrations: updatedIntegrations }
        })

        queryClient.setQueryData(['externalIntegrations', workspaceId, 'metrics'], (old: Data | undefined) => {
          const updatedIntegrations =
            old?.external_integrations?.filter((i: ExternalIntegration) => !ids.includes(i.id)) || []

          return { ...old, external_integrations: updatedIntegrations }
        })

        return { previousIntegrations, previousIntegrationsWithMetrics }
      },
      onSuccess: (data, variables, context) => {
        options?.onSuccess?.(data, variables, context)
        queryClient.invalidateQueries(['externalIntegrations', workspaceId])
        queryClient.invalidateQueries(calendarKeys.all)
        queryClient.invalidateQueries(scheduledPostsKeys.index(workspaceId))
        queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', 'detail'])
        queryClient.resetQueries(['workspace', workspaceId, 'files'])
      },
      onError: (err, variables, context) => {
        options?.onError?.(err, variables, context)
        queryClient.setQueryData(['externalIntegrations', workspaceId], context?.previousIntegrations)
        queryClient.setQueryData(
          ['externalIntegrations', workspaceId, 'metrics'],
          context?.previousIntegrationsWithMetrics,
        )
      },
    },
  )
}
